import React from 'react';

const SearchForm = ({ 
  query, 
  setQuery, 
  searchType, 
  loading, 
  user, 
  userUsage, 
  showSearchTips, 
  setShowSearchTips,
  handleSearch 
}) => {
  return (
    <form onSubmit={handleSearch} style={{ marginBottom: '32px' }}>
      {/* Search Input Row */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }} data-tour="search-box">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            searchType === 'ai' ? "Describe what you're looking for (or use AND/OR/NOT)..." :
            searchType === 'patent' ? "Enter patent number (e.g., 11856881)" :
            searchType === 'assignee' ? "Enter company name (e.g., Pfizer)" :
            "Enter inventor name (e.g., Smith)"
          }
          style={{
            flex: 1,
            padding: '14px 18px',
            borderRadius: '8px',
            border: '2px solid #e0e0e0',
            fontSize: '16px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = '#1976d2'}
          onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '14px 28px',
            borderRadius: '8px',
            border: 'none',
            background: loading ? '#94a3b8' : '#1976d2',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* AI Search remaining counter */}
      {searchType === 'ai' && user && userUsage?.ai_searches && (
        <div style={{ fontSize: '13px', color: '#5e778a', marginTop: '4px' }}>
          {userUsage.ai_searches.limit < 9999 ? (
            <>AI searches: {userUsage.ai_searches.remaining} of {userUsage.ai_searches.limit} remaining today</>
          ) : (
            <span style={{ color: '#10b981' }}>Unlimited AI searches</span>
          )}
        </div>
      )}

      {/* Advanced Search Tips - Only shown for AI Search */}
      {searchType === 'ai' && (
        <div style={{ marginTop: '8px' }}>
          <button
            type="button"
            onClick={() => setShowSearchTips(!showSearchTips)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 0'
            }}
          >
            <span style={{
              display: 'inline-block',
              transform: showSearchTips ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>▶</span>
            Advanced Search Tips
          </button>

          {showSearchTips && (
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '8px',
              fontSize: '13px',
              lineHeight: '1.6'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px'
              }}>
                {/* Boolean Operators Column */}
                <div>
                  <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                    Boolean Operators
                  </div>
                  <div style={{ color: '#475569' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        battery AND thermal
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Both terms required</div>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        battery OR capacitor
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Either term</div>
                    </div>
                    <div>
                      <code style={{ background: '#fce7f3', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        battery NOT lithium
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Exclude term</div>
                    </div>
                  </div>
                </div>

                {/* Field-Specific Column */}
                <div>
                  <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                    Field-Specific
                  </div>
                  <div style={{ color: '#475569' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <code style={{ background: '#d1fae5', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        title:electric
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Title only</div>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <code style={{ background: '#d1fae5', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        claims:thermal
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Claims only</div>
                    </div>
                    <div>
                      <code style={{ background: '#d1fae5', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                        abstract:cooling
                      </code>
                      <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Abstract only</div>
                    </div>
                  </div>
                </div>

                {/* Proximity + Tip Column */}
                <div>
                  <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                    Proximity
                  </div>
                  <div style={{ color: '#475569', marginBottom: '12px' }}>
                    <code style={{ background: '#ede9fe', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                      "fuel cell"~5
                    </code>
                    <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>Words within 5 of each other</div>
                  </div>

                  <div style={{
                    padding: '8px',
                    background: '#fefce8',
                    borderRadius: '4px',
                    border: '1px solid #fde047'
                  }}>
                    <div style={{ fontWeight: '600', color: '#854d0e', fontSize: '12px' }}>💡 Pro Tip</div>
                    <div style={{ fontSize: '11px', color: '#713f12', marginTop: '2px' }}>
                      Boolean syntax bypasses AI for faster, broader results
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default SearchForm;
